import { useState, useEffect } from 'react';

/**
 * Hook to listen for changes to a specific key in sessionStorage
 * @param key The key to listen for in sessionStorage
 * @returns The current value of the key in sessionStorage
 */
export const useSessionStorage = (key: string): string | null => {
  const [value, setValue] = useState<string | null>(() => {
    return sessionStorage.getItem(key);
  });

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key) {
        setValue(e.newValue);
      }
    };

    const handleStorageChangeDirect = () => {
      setValue(sessionStorage.getItem(key));
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('sessionStorageChange', handleStorageChangeDirect);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sessionStorageChange', handleStorageChangeDirect);
    };
  }, [key]);

  return value;
};

/**
 * Helper function to set a value in sessionStorage and dispatch a custom event,
 * notifying listeners of the above hook of the change
 * @param key The key to set in sessionStorage
 * @param value The value to set
 */
export const setSessionStorageItem = (key: string, value: string): void => {
  sessionStorage.setItem(key, value);
  window.dispatchEvent(new Event('sessionStorageChange'));
};

export const deleteSessionStorageItem = (key: string): void => {
  sessionStorage.removeItem(key);
  window.dispatchEvent(new Event('sessionStorageChange'));
};
